import { useAddToFavorites, useRemoveFromFavorites } from '~/api/queries/favorites';

export const useFavoritesManager = () => {
  const authStore = useAuthStore();
  const favorites = ref<Set<string>>(new Set());
  const loadingCards = ref<Set<string>>(new Set());
  const isInitialized = ref(false);

  const isLoggedIn = computed(() => !!authStore.getToken && !!authStore.getUser);

  const { mutateAsync: addToFavorites } = useAddToFavorites();
  const { mutateAsync: removeFromFavorites } = useRemoveFromFavorites();

  const loadAllFavorites = async (limit = 1000) => {
    if (!isLoggedIn.value || !authStore.user?.id) {
      favorites.value = new Set();
      isInitialized.value = true;
      return;
    }

    try {
      const response = await $fetch<{
        success?: boolean;
        data?: {
          favorited_ids?: string[];
          hasMore?: boolean;
        };
        favorited_ids?: string[];
      }>('/api/favorites/all', {
        method: 'GET',
        params: {
          limit,
        },
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      });

      let favoritedIds: string[] = [];

      if (response?.data?.favorited_ids) {
        favoritedIds = response.data.favorited_ids;
      } else if (Array.isArray(response?.favorited_ids)) {
        favoritedIds = response.favorited_ids;
      } else if (Array.isArray(response)) {
        favoritedIds = response;
      }

      favorites.value = new Set(favoritedIds);
      isInitialized.value = true;
    } catch (error) {
      favorites.value = new Set();
      isInitialized.value = true;
    }
  };

  const initializeFavorites = async (propertyIds: string[]) => {
    if (!isLoggedIn.value || !authStore.user?.id) {
      favorites.value = new Set();
      isInitialized.value = true;
      return;
    }

    const normalizedIds = propertyIds.map((id) => String(id)).filter(Boolean);

    if (normalizedIds.length === 0) {
      favorites.value = new Set();
      isInitialized.value = true;
      return;
    }

    try {
      const response = await $fetch<{
        success?: boolean;
        data?: {
          favorited_ids?: string[];
        };
      }>('/api/favorites/batch-status', {
        method: 'POST',
        body: {
          complex_ids: normalizedIds,
        },
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      });

      const favoritedIds = response?.data?.favorited_ids || [];

      if (!isInitialized.value) {
        favorites.value = new Set(favoritedIds);
      } else {
        favoritedIds.forEach((id: string) => favorites.value.add(id));
      }

      isInitialized.value = true;
    } catch (error) {
      favorites.value = new Set();
      isInitialized.value = true;
    }
  };

  const toggleFavorite = async (complexId: string) => {
    const normalizedId = String(complexId);
    if (!isLoggedIn.value || !authStore.user?.id) {
      await navigateTo('/login');
      return;
    }

    if (loadingCards.value.has(normalizedId)) return;

    const isCurrentlyFavorite = favorites.value.has(normalizedId);
    const previousState = isCurrentlyFavorite;

    try {
      loadingCards.value.add(normalizedId);

      if (previousState) {
        await removeFromFavorites({ complexId: normalizedId, userId: authStore.user.id.toString() });
        favorites.value.delete(normalizedId);
      } else {
        await addToFavorites({ complex_id: normalizedId });
        favorites.value.add(normalizedId);
      }
    } catch (error: unknown) {
      const err = error as { status?: number };
      if (err?.status === 409) {
        if (!previousState) {
          favorites.value.add(normalizedId);
        }
      } else if (err?.status === 404) {
        if (previousState) {
          favorites.value.delete(normalizedId);
        }
      } else {
        if (previousState) {
          favorites.value.add(normalizedId);
        } else {
          favorites.value.delete(normalizedId);
        }
        console.error('Failed to toggle favorite:', error);
      }
    } finally {
      loadingCards.value.delete(normalizedId);
    }
  };

  const isFavorite = (complexId: string) => {
    return favorites.value.has(String(complexId));
  };

  const isLoading = (complexId: string) => {
    return loadingCards.value.has(String(complexId));
  };

  return {
    favorites: readonly(favorites),
    loadingCards: readonly(loadingCards),
    isLoggedIn,
    initializeFavorites,
    loadAllFavorites,
    toggleFavorite,
    isFavorite,
    isLoading,
    isInitialized: readonly(isInitialized),
  };
};
